import type { Slot } from '../../data/schema';

export interface MetaBonuses {
  hpFlat: number; hpPct: number; damagePct: number; kamasPct: number; rerolls: number; shopDiscountPct: number;
  startItems: Slot[]; cooldownPct: number; speedPct: number; xpPct: number; startKamas: number; pickupRange: number;
  shopRerollFree: number; startSpells: string[]; rewindCooldown: number; slowBonusPct: number; startLevel: number;
  refletsPct: number; shieldOnRoom: number; bossDamagePct: number;
  unlockedSpells: string[] | null; unlockedSets: string[] | null; pet: string | null; roomModifiers: string[];
}

export const ZERO_META_BONUSES: Readonly<MetaBonuses> = Object.freeze({
  hpFlat: 0, hpPct: 0, damagePct: 0, kamasPct: 0, rerolls: 0, shopDiscountPct: 0,
  startItems: [], cooldownPct: 0, speedPct: 0, xpPct: 0, startKamas: 0, pickupRange: 0,
  shopRerollFree: 0, startSpells: [], rewindCooldown: 0, slowBonusPct: 0, startLevel: 0,
  refletsPct: 0, shieldOnRoom: 0, bossDamagePct: 0,
  unlockedSpells: null, unlockedSets: null, pet: null, roomModifiers: [],
}) as Readonly<MetaBonuses>;

type NumKey = { [K in keyof MetaBonuses]: MetaBonuses[K] extends number ? K : never }[keyof MetaBonuses];

const PCT_KEYS: NumKey[] = ['hpPct', 'damagePct', 'kamasPct', 'shopDiscountPct', 'cooldownPct', 'speedPct', 'xpPct', 'slowBonusPct', 'refletsPct', 'bossDamagePct'];
const RANGES: Partial<Record<NumKey, [number, number]>> = {
  startLevel: [0, 5], rerolls: [0, 10], shopRerollFree: [0, 10], hpFlat: [-500, 5000], pickupRange: [0, 400],
  rewindCooldown: [0, 5], shieldOnRoom: [0, 500], startKamas: [0, 5000],
};
for (const k of PCT_KEYS) RANGES[k] = [-90, 300];

const NUM_KEYS = Object.keys(ZERO_META_BONUSES).filter((k) => typeof ZERO_META_BONUSES[k as keyof MetaBonuses] === 'number') as NumKey[];

/** Borne chaque bonus à sa plage légale ; NaN devient 0. */
export function clampBonuses(b: MetaBonuses): MetaBonuses {
  const out: MetaBonuses = { ...b, startItems: [...b.startItems], startSpells: b.startSpells.slice(0, 4), roomModifiers: [...b.roomModifiers] };
  for (const k of NUM_KEYS) {
    const v = Number.isNaN(b[k]) ? 0 : b[k];
    const r = RANGES[k];
    out[k] = r ? Math.min(r[1], Math.max(r[0], v)) : v;
  }
  return out;
}

/** Additionne les nombres, concatène les tableaux ; pet et unlocked* de b remplacent ceux de a s'ils sont définis. */
export function mergeBonuses(a: MetaBonuses, b: Partial<MetaBonuses>): MetaBonuses {
  const out: MetaBonuses = { ...a, startItems: [...a.startItems, ...(b.startItems ?? [])], startSpells: [...a.startSpells, ...(b.startSpells ?? [])], roomModifiers: [...a.roomModifiers, ...(b.roomModifiers ?? [])] };
  for (const k of NUM_KEYS) out[k] = a[k] + (b[k] ?? 0);
  if (b.pet !== undefined) out.pet = b.pet;
  if (b.unlockedSpells !== undefined) out.unlockedSpells = b.unlockedSpells;
  if (b.unlockedSets !== undefined) out.unlockedSets = b.unlockedSets;
  return out;
}

/** PV max d'un personnage dont la base (PV de classe + Vitalité) vaut `base` : pourcentage puis bonus fixe, au moins 1. */
export function maxHpWith(base: number, b: MetaBonuses): number {
  return Math.max(1, Math.round(base * (1 + b.hpPct / 100)) + b.hpFlat);
}
